import { CategoryFragment } from '@gql/_generated/categories.generated';
import { EntriesFragment } from '@gql/_generated/entries.generated';
import client from '@lib/fetch/client';
import { withSite } from '@lib/hooks';
import { parseApiError } from '@lib/parse/parseApiError';
import { firstNonNullable } from '@liquorice/allsorts-craftcms-nextjs';
import { gql } from 'graphql-request';
import { Category, CategoryTypeId, parseCategories } from '../categories';
import { Entry, EntrySectionId, EntryTypeId, parseEntries } from '../entries';
import {
  EntryIndexQueryProps,
  EntryIndexQueryResult,
  prepareEntryIndexArgs,
} from './getEntryIndexData';
import { getSdk, TicketIndexQueryVariables } from './_generated/getTicketIndexData.generated';

gql`
  query ticketIndexEntries($site: [String] = "default", $limit: Int = 4) {
    ticketIndexEntries(site: $site) {
      ... on ticketIndex_ticketIndex_Entry {
        eventMultiple(limit: $limit) {
          ...eventCard
        }
      }
    }
  }
`;

gql`
  query ticketIndex(
    $site: [String] = "default"
    $query: String
    $includeCategories: Boolean = false
    $entryLimit: Int = 12
    $entryOffset: Int = 0
    $idQuery: [QueryArgument]
    $categoryLimit: Int = 99
    $entrySection: [String]
    $entryType: [String]
    $categoryType: [String]
    $entryCategoryIds: [QueryArgument]
    $entryCategoryQuery: [CategoryCriteriaInput]
    $relatedToEntries: [EntryCriteriaInput]
    $entryOrderBy: String
    $categoryOrderBy: String
    # Event only queries
    $eventDateStart: [QueryArgument] = null
    $eventDateEnd: [QueryArgument] = null
    $indexDisplay: [QueryArgument] = null
  ) {
    entries: entries(
      site: $site
      id: $idQuery
      search: $query
      section: $entrySection
      type: $entryType
      relatedTo: $entryCategoryIds
      relatedToCategories: $entryCategoryQuery
      relatedToEntries: $relatedToEntries
      limit: $entryLimit
      offset: $entryOffset
      orderBy: $entryOrderBy
      eventDateStart: $eventDateStart
      eventDateEnd: $eventDateEnd
      indexDisplay: $indexDisplay
    ) {
      ...entryCard
    }

    categories: categories(
      site: $site
      search: $query
      group: $categoryType
      limit: $categoryLimit
      orderBy: $categoryOrderBy
    ) @include(if: $includeCategories) {
      ...category
    }

    entryCount(
      site: $site
      search: $query
      section: $entrySection
      type: $entryType
      relatedTo: $entryCategoryIds
      relatedToCategories: $entryCategoryQuery
      relatedToEntries: $relatedToEntries
      eventDateStart: $eventDateStart
      eventDateEnd: $eventDateEnd
      id: $idQuery
      indexDisplay: $indexDisplay
    )
  }
`;

export const getTicketIndexData = async <
  E extends EntrySectionId,
  T extends EntryTypeId,
  C extends CategoryTypeId
>(
  maybeArgs: EntryIndexQueryProps<E, T, C>,
  customVariables?: TicketIndexQueryVariables
) => {
  const sdk = getSdk(client);

  const indexRepsonse = await sdk
    .ticketIndexEntries(
      withSite({
        site: customVariables?.site,
      })
    )
    .catch((err) => {
      console.warn(parseApiError(err));
    });

  const { ticketIndexEntries } = indexRepsonse ?? {};

  const ticketIndex = firstNonNullable(ticketIndexEntries);

  const featuredEntries = parseEntries(
    ticketIndex?.eventMultiple as EntriesFragment[]
  ) as Entry<E>[];

  // Should be no need to slice here, but just in case
  const featuredEntryIds = featuredEntries.map((v) => v.id).slice(0, 4);

  const isFiltered = !!customVariables && Object.keys(customVariables).length > 0;

  const { args, variables } = prepareEntryIndexArgs(maybeArgs, {
    ...customVariables,
    indexDisplay: ['ticketIndex', 'both'],
    idQuery: isFiltered ? undefined : ['not', ...featuredEntryIds],
  });

  const ticketRepsonse = await sdk.ticketIndex(withSite(variables)).catch((err) => {
    console.warn(parseApiError(err));
  });

  const {
    entries: maybeEntries,
    categories: maybeCategories,
    entryCount = 0,
  } = ticketRepsonse ?? {};

  const entries = parseEntries(
    maybeEntries as EntriesFragment[] /* , entrySection */
  ) as Entry<E>[];

  const categories = (args.categoryType ?? []).reduce((results, catId) => {
    return {
      ...results,
      [catId]: parseCategories(maybeCategories as CategoryFragment[], catId),
    };
  }, {} as { [P in C]: Category<C>[] });

  const result: EntryIndexQueryResult<E, T, C> = {
    args,
    entries,
    featuredEntries,
    categories,
    entryCount,
    page: args.page ?? 1,
    pages: variables.entryLimit ? Math.ceil(entryCount / variables.entryLimit) : 1,
  };

  return result;
};
